import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useCreatePromptMutation } from "@/store/admin_api";
import type { Prompt, PromptKind, SkillShape } from "@/model/interface/Prompt";
import { apiErrorMessage } from "@/util/apiError";
import SkillShapeFields, { EMPTY_SKILL_SHAPE } from "./SkillShapeFields";

export const CREATE_PROMPT_COPY: Record<PromptKind, { title: string; description: string; namePlaceholder: string; descriptionPlaceholder: string; created: string }> = {
  AGENT: {
    title: "New prompt",
    description: "Creates a prompt with its first (v1) version.",
    namePlaceholder: "e.g. Inbound Receptionist",
    descriptionPlaceholder: "What this prompt is for",
    created: "Prompt created",
  },
  SKILL: {
    title: "New skill",
    description: "Creates a skill with its first (v1) version.",
    namePlaceholder: "e.g. Callback discipline",
    descriptionPlaceholder: "When this skill applies. Shown when picking skills for an agent",
    created: "Skill created",
  },
};

const EMPTY_DETAILS = { name: "", description: "", versionLabel: "", systemPrompt: "" };

export default function CreatePromptDialog({
  kind,
  open,
  onOpenChange,
  onCreated,
}: {
  kind: PromptKind;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (prompt: Prompt) => void;
}) {
  const [createPrompt, { isLoading: isCreating }] = useCreatePromptMutation();
  const [details, setDetails] = useState(EMPTY_DETAILS);
  const [shape, setShape] = useState<SkillShape>(EMPTY_SKILL_SHAPE);

  const copy = CREATE_PROMPT_COPY[kind];

  const handleCreate = async () => {
    if (!details.name.trim() || !details.systemPrompt.trim()) {
      toast.error("Name and system prompt are required");
      return;
    }
    const isSkill = kind === "SKILL";
    const isPhase = isSkill && shape.skillType === "PHASE";
    if (isPhase && !shape.handoffDescription.trim()) {
      toast.error("A call stage needs a hand-off description");
      return;
    }
    try {
      const created = await createPrompt({
        name: details.name.trim(),
        description: details.description.trim() || undefined,
        systemPrompt: details.systemPrompt,
        versionLabel: details.versionLabel.trim() || undefined,
        kind,
        skillType: isSkill ? shape.skillType : undefined,
        skillScope: isSkill && !isPhase ? shape.skillScope : undefined,
        skillAudience: isSkill ? shape.skillAudience : undefined,
        memberTools: isPhase ? shape.memberTools : undefined,
        handoffDescription: isPhase ? shape.handoffDescription.trim() : undefined,
        openingLine: isPhase ? shape.openingLine.trim() || undefined : undefined,
      }).unwrap();
      toast.success(copy.created);
      setDetails(EMPTY_DETAILS);
      setShape(EMPTY_SKILL_SHAPE);
      onOpenChange(false);
      onCreated(created);
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to create"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Name *</Label>
            <Input
              value={details.name}
              onChange={(event) => setDetails({ ...details, name: event.target.value })}
              placeholder={copy.namePlaceholder}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Input
              value={details.description}
              onChange={(event) => setDetails({ ...details, description: event.target.value })}
              placeholder={copy.descriptionPlaceholder}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Version label</Label>
            <Input
              value={details.versionLabel}
              onChange={(event) => setDetails({ ...details, versionLabel: event.target.value })}
              placeholder="v1 (optional)"
            />
          </div>
          {kind === "SKILL" && (
            <SkillShapeFields shape={shape} onChange={(next) => setShape({ ...shape, ...next })} />
          )}
          <div className="space-y-1.5">
            <Label>System prompt *</Label>
            <Textarea
              rows={14}
              value={details.systemPrompt}
              onChange={(event) => setDetails({ ...details, systemPrompt: event.target.value })}
              className="font-mono text-xs"
              placeholder="System prompt text…"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={isCreating}>
            {isCreating ? "Creating…" : copy.title}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
